/**
 * Banner entity class
 */

/**
 * BannerFactory
 * returns Banner entity class
 *
 * @class BannerFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Banner} class
 *
 * @ngdoc factory
 * @name mwApp.factory:BannerFactory
 */
/* @ngInject */
export default function BannerFactory ($injector, $sce, $http, Type, Entity, BannerBundle, alertLiteSvc, onOffMixin) {
  /* === Banner class === */

  /**
   * Banner model
   *
   * @class Banner
   * @classdesc Banner model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @return {models.Banner} Banner entity
   */
  function Banner () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Banner.prototype = Object.create(Entity.prototype)
  Banner.prototype.constructor = Banner

  /* === Banner instance methods & fields === */

  Object.assign(Banner.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Banner#
     * @type {Object}
     */
    _fields: {
      id: null,
      autoScrollEnabled: false,
      autoScrollIn: 5,
      bannerBundles: ['BannerBundle'],
      enabled: false,
      networkId: Number,
      reassignSubscriptions: Boolean,
      subscriptionName: String,
      subscriptions: [{
        id: Number,
        name: String
      }],
      $action: null,
      $listOfSubscriptions: [{
        id: Number,
        name: String,
        networkId: Number
      }],
      $selectedSubscription: {
        id: Number,
        name: String
      }
    },

    /**
     * url template
     *
     * @memberof models.Banner#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/subscriptions/carousels/banners/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Banner#
     * @type {String}
     */
    _publicUrlTemplate: 'networks/{{::networkId}}/customization',

    /**
     * Prepare note
     *
     * @memberof models.Banner#
     * @override
     */
    _prepare: function () {
      this.bannerBundles.forEach(bannerBundle => { bannerBundle.$carouselId = this.id })
    },

    /**
     * Get underlying object
     *
     * @memberof models.Banner#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Get banner platforms
     *
     * @memberof models.Banner#
     */
    _getContent: function (tableState, o) {
      if (!this._url) {
        return this._updateUrl()
          .then(() => this._getContentTypes())
      }

      var params = {
        'sort-by': tableState.sort ? tableState.sort : '',
        page: tableState.page || 0,
        count: tableState.count
      }

      if (Object.prototype.hasOwnProperty.call(tableState, 'q')) {
        params.q = tableState.q
      }
      return $http.get(`api/networks/${this.networkId}/subscriptions/carousels/banners/content`, { params: Object.assign(params, o) })
        .data()
        .then((response) => response.payload)
        .catch(rejection => alertLiteSvc.alertError(rejection))
    }
  }, onOffMixin)

  /* === Banner static methods & fields === */

  /**
   * Generate url
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {String} [url]
   * @return {String}
   */
  Banner.generateUrl = function (url = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    return `${cast.prototype.$_urlTemplate(o)}` + url
  }

  /**
   * Find all platforms of package
   *
   * @param {Number} networkId
   * @param {[Number]} subscriptionIds
   * @return {Promise} promise
   */
  Banner.getPlatforms = function (networkId, subscriptionIds) {
    return $http.get(`api/networks/${networkId}/subscriptions/carousels/banners/platforms`, { params: { subscriptionIds } })
      .then((response) => response.data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Find all content types
   *
   * @param {Number} networkId
   * @return {Promise} promise
   */
  Banner.getContentTypes = function (networkId) {
    return $http.get(`api/networks/${networkId}/subscriptions/0/carousels/banners/content-types`)
      .then((response) => response.data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * get subscriptions for content
   *
   * @param {Number} networkId
   * @param {Number} contentId
   * @param {String} contentType
   * @return {Promise} promise
   */
  Banner.getSubscriptionsByContent = function (networkId, contentId, contentType) {
    return $http.get(`api/networks/${networkId}/subscriptions/carousels/banners/content/${contentId}/subscriptions`, { params: { contentType } })
      .then((response) => response.data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get available Subscriptions
   *
   * @param {Number} networkId
   * @return {Promise} promise
   */
  Banner.getAvailableSubscriptions = function (networkId) {
    return $http.get(`api/networks/${networkId}/subscriptions?carouselType=BANNER`)
      .then((response) => response.data)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  // copy all static methods from Entity class
  Object.assign(Banner, Entity)
  Banner._name = 'Banner'
  Banner.init()

  // permissions
  Banner.access = {
    view: 'viewBannerCarousel',
    edit: 'editBannerCarousel',
    create: 'editBannerCarousel',
    delete: 'deleteBannerCarousel'
  }

  return Banner
}
